import { fetchCommunityPlaces } from "./communities"
import { initialsOf } from "./watchlist"
import { buildFullName } from "./helpers"

// Shared logic for the Checker (owner) and Convoy (companion) pages, kept
// pure/testable and mirroring the Android app's CheckerScreen.kt/
// ConvoyScreen.kt so both clients agree on what a trip's state means.

/** Parses an API timestamp (ISO string, "YYYY-MM-DD HH:mm:ss", epoch number,
 * or Date) into a Date, or null if missing/unparseable. Naive strings with
 * no timezone are treated as UTC, matching how the database stores them. */
export function parseApiDateTime(value) {
    if (!value) return null

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }

    if (typeof value === "number") {
        const fromNumber = new Date(value)
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber
    }

    if (typeof value !== "string") return null

    let raw = value.trim()
    if (!raw) return null

    // Trim sub-millisecond precision (Safari rejects more than 3 fraction digits).
    raw = raw.replace(/\.(\d{3})\d+/, (match, milliseconds) => `.${milliseconds}`)

    const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized)
    const withZ = hasTimezone ? normalized : `${normalized}Z`

    const parsed = new Date(withZ)
    if (!Number.isNaN(parsed.getTime())) return parsed

    const fallback = new Date(raw)
    return Number.isNaN(fallback.getTime()) ? null : fallback
}

/** "September 22, 2026 4:35 PM" in the viewer's own timezone. */
export function formatDateTime(value) {
    const date = parseApiDateTime(value)
    if (!date) return "-"

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    const datePart = new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone,
    }).format(date)
    const timePart = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    }).format(date)

    return `${datePart} ${timePart}`
}

/** "Sep 22, 4:35 pm" -- matches the Android app's compact check-in timestamp
 * (formatCheckinDateTime in CheckerScreen.kt): includes the date, since a
 * trip spanning more than a day needs to say which day a check-in landed
 * on, and lowercases only the am/pm marker, not the month abbreviation. */
export function formatCheckinDateTime(value) {
    const date = parseApiDateTime(value)
    if (!date) return "None"

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    const formatted = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    }).format(date)

    return formatted.replace("AM", "am").replace("PM", "pm")
}

/** "1:23:45", "23:45" or "Overdue" for a countdown, matching the Android
 * app's formatCountdown. */
export function formatCountdown(remainingMs) {
    if (remainingMs <= 0) return "Overdue"

    const totalSeconds = Math.floor(remainingMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n) => String(n).padStart(2, "0")

    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(minutes)}:${pad(seconds)}`
}

/**
 * Derives a trip's real display status and overdue state from its raw
 * timestamps, live against `now` rather than trusting a possibly-stale
 * `is_overdue`/`status` snapshot from the last fetch. The backend may
 * activate a trip before its start_time, or leave a started trip marked
 * scheduled, so this is the source of truth both cards render from.
 *
 * @param {Object} trip - Raw trip fields (status, start_time, next_checkin_due_at)
 * @param {number} [now] - Epoch ms, defaults to Date.now()
 * @returns {{ displayStatus: string, isOverdue: boolean, dueMillis: number|null, hasValidNextCheckin: boolean }}
 */
export function deriveCheckerStatus(trip, now = Date.now()) {
    const startMillis = parseApiDateTime(trip?.start_time)?.getTime() ?? null
    const dueMillis = parseApiDateTime(trip?.next_checkin_due_at)?.getTime() ?? null

    let displayStatus = trip?.status || "unknown"
    if (displayStatus === "active" && startMillis !== null && now < startMillis) {
        displayStatus = "scheduled"
    } else if (
        displayStatus === "scheduled" &&
        startMillis !== null &&
        now >= startMillis
    ) {
        displayStatus = "active"
    }

    // A due time before start_time means the backend computed it before the
    // trip actually began, so it isn't meaningful to show or race against yet.
    const hasValidNextCheckin =
        dueMillis !== null && startMillis !== null && dueMillis >= startMillis

    return {
        displayStatus,
        isOverdue: hasValidNextCheckin && now > dueMillis,
        dueMillis: hasValidNextCheckin ? dueMillis : null,
        hasValidNextCheckin,
    }
}

/**
 * The progress-pill row shared by both cards: one entry per expected
 * check-in, each either "done" (safe), "attention" (done while overdue/in
 * danger) or "empty" (not yet due). Clears back to all-safe the moment a
 * fresh "safe" check-in lands, since completed/needsAttention are both
 * recomputed from the trip's current fields, not accumulated separately.
 *
 * @param {number} expectedCount
 * @param {number} completedCount
 * @param {boolean} needsAttention - true while overdue or safety_status is "danger"
 * @returns {("done"|"attention"|"empty")[]}
 */
export function checkerProgressPills(expectedCount, completedCount, needsAttention) {
    const total = Math.max(0, Number(expectedCount) || 0)
    const completed = Math.max(0, Number(completedCount) || 0)

    return Array.from({ length: total }, (_, i) =>
        i >= completed ? "empty" : needsAttention ? "attention" : "done",
    )
}

/**
 * Candidate companions to invite to a trip: every person across the user's
 * communities, excluding themselves, deduped by member id and sorted by
 * name -- mirrors the Android app's CreateTripScreen/EditTripScreen
 * (CommunityRepository.getCommunities().flatMap { it.allPeople }...).
 *
 * @param {number|string} currentUserId
 * @returns {Promise<{ memberId: string, name: string, initials: string, profilePictureUrl: string|null }[]>}
 */
export async function getInvitableCompanions(currentUserId) {
    const communities = await fetchCommunityPlaces(currentUserId)
    const seen = new Set()
    const companions = []

    for (const community of communities || []) {
        for (const person of community.persons || []) {
            const memberId = person.member_id ?? person.id
            if (
                memberId == null ||
                String(memberId) === String(currentUserId) ||
                seen.has(String(memberId))
            ) {
                continue
            }
            seen.add(String(memberId))

            const name =
                person.full_name ||
                buildFullName(person.first_name, person.last_name) ||
                person.username ||
                person.email ||
                "Unknown"

            companions.push({
                memberId: String(memberId),
                name,
                initials: initialsOf(name),
                profilePictureUrl: person.profile_picture_url || null,
            })
        }
    }

    return companions.sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * A trip's companion rows (from getCheckerCompanions), narrowed to the
 * owner's actual convoy -- excludes the owner's own row and anyone who
 * declined or was removed, matching Android's own filter everywhere it
 * reads this list.
 */
export function activeCheckerCompanions(companions) {
    return (companions || []).filter(
        (c) => c.is_companion === 1 && c.status !== "removed" && c.status !== "declined",
    )
}

/** The subset who've actually confirmed -- also who gets an avatar shown. */
export function acceptedCheckerCompanions(companions) {
    return activeCheckerCompanions(companions).filter((c) => c.status === "accepted")
}

/** Display name for a companion row (first/last name, else username). */
export function checkerCompanionName(companion) {
    return (
        buildFullName(companion?.first_name, companion?.last_name) ||
        companion?.username ||
        "Unknown"
    )
}

/**
 * A Convoy trip owner's current whereabouts (owner_entity_name/_country from
 * GET /v1/checker/convoy): "Milton, Canada", else "Milton", else "Location
 * unavailable". Mirrors Android's TripInfo.ownerLocationLabel.
 */
export function convoyOwnerLocationLabel(trip) {
    const name = trip?.owner_entity_name?.trim()
    if (!name) return "Location unavailable"
    const country = trip?.owner_entity_country?.trim()
    return country && country !== name ? `${name}, ${country}` : name
}

/**
 * "I'm safe" stays enabled once its window opens (grace_minutes before the
 * next check-in is due) and forever after, including once overdue -- an
 * owner who's already overdue must still be able to check in. It disables
 * only in the narrow window right after a "safe" check-in already covers
 * the current cycle. Mirrors Android's ActiveTripCard imSafeEnabled.
 *
 * @param {Object} trip - Raw trip fields (safety_status, grace_minutes)
 * @param {number|null} dueMillis - From deriveCheckerStatus
 * @param {number} now - Epoch ms
 */
export function isImSafeEnabled(trip, dueMillis, now) {
    if (trip?.safety_status !== "safe") return true
    if (dueMillis == null) return true

    const graceMillis = (Number(trip?.grace_minutes) || 0) * 60_000
    return now >= dueMillis - graceMillis
}
